import Phaser from 'phaser';
import { EntityManager } from '../entities/EntityManager';
import { Player } from '../entities/Player';
import { Tower } from '../entities/Tower';
import { TreeSlot } from '../entities/TreeSlot';
import { PlayerId } from './InputRouter';
import { RENDER_LAYERS } from './RenderSystem';
import { COLORS } from '../constants';

// Dance progress config (matches TowerInteractionSystem's rate)
const DANCE_CONFIG = {
  baseTimeSeconds: 22.5,         // Base time for level 1->2 (50% longer than original 15s)
  timeMultiplierPerLevel: 1.5,   // Each level takes 1.5x longer
  maxLevel: 6,                    // Black = 5, Rainbow (dance-only) = 6
  rainbowTimeMultiplier: 10,     // Level 5->6 (rainbow) takes 10x longer than 4->5
};

// ============================================
// Dance System
// Based on tower_leveling_and_bosses.md
// ============================================

interface DancingPlayer {
  playerId: PlayerId;
  towerId: string;
  startTime: number;
}

class DanceSystemManager {
  private scene: Phaser.Scene | null = null;
  private dancingPlayers: Map<PlayerId, DancingPlayer> = new Map();

  // Dance visuals
  private danceParticles: Map<PlayerId, Phaser.GameObjects.Particles.ParticleEmitter> = new Map();
  private danceTexts: Map<PlayerId, Phaser.GameObjects.Text> = new Map();
  private expBars: Map<string, Phaser.GameObjects.Container> = new Map();

  // Callbacks
  public onDanceStart?: (playerId: PlayerId, towerId: string) => void;
  public onDanceEnd?: (playerId: PlayerId, towerId: string) => void;

  init(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  /**
   * Try to start dancing on the nearest tower
   */
  tryStartDance(playerId: PlayerId): boolean {
    if (this.isDancing(playerId)) return false;

    const player = this.getPlayer(playerId);
    if (!player) return false;

    // Find tower at player's location
    const tower = this.findTowerAtPlayer(player);
    if (!tower) return false;

    // Check if tower is at max level
    if (tower.getLevel() >= DANCE_CONFIG.maxLevel) {
      this.showMaxLevelIndicator(tower);
      return false;
    }

    // Start dancing
    this.startDance(playerId, tower.id);
    return true;
  }

  /**
   * Stop dancing
   */
  stopDance(playerId: PlayerId): void {
    const dancing = this.dancingPlayers.get(playerId);
    if (!dancing) return;

    // Clean up visuals
    this.cleanupDanceVisuals(playerId);

    // Remove from dancing list
    this.dancingPlayers.delete(playerId);

    // Re-enable player movement
    const player = this.getPlayer(playerId);
    if (player) {
      player.setDancing(false);
    }

    // Hide exp bar
    this.hideExpBar(dancing.towerId);

    // Callback
    this.onDanceEnd?.(playerId, dancing.towerId);

    console.log(`[DanceSystem] Player ${playerId} stopped dancing on tower ${dancing.towerId}`);
  }

  /**
   * Check if player is currently dancing
   */
  isDancing(playerId: PlayerId): boolean {
    return this.dancingPlayers.has(playerId);
  }

  /**
   * Get the tower a player is dancing on
   */
  getDancingTowerId(playerId: PlayerId): string | null {
    return this.dancingPlayers.get(playerId)?.towerId || null;
  }

  /**
   * Update all dancing players (call each frame)
   */
  update(delta: number): void {
    const deltaSeconds = delta / 1000;

    // Group dancers by tower to detect co-op dancing
    const towerDancers = new Map<string, PlayerId[]>();
    for (const [playerId, dancing] of this.dancingPlayers) {
      const dancers = towerDancers.get(dancing.towerId) || [];
      dancers.push(playerId);
      towerDancers.set(dancing.towerId, dancers);
    }

    // Apply dance progress (uses same system as TowerInteractionSystem and kills)
    for (const [towerId, dancers] of towerDancers) {
      const tower = EntityManager.get<Tower>(towerId);
      if (!tower) continue;

      const coOpDancing = dancers.length > 1;
      
      // Calculate progress amount based on time and level
      const level = tower.getLevel();
      const upgradeTime = this.getUpgradeTime(level);
      let progressAmount = deltaSeconds / (upgradeTime / 1000);
      
      // Multiple dancers = proportionally faster (2 people = 2x, 3 = 3x)
      progressAmount *= dancers.length;
      
      // Add progress to tower (shared with kills and TowerInteractionSystem)
      tower.addUpgradeProgress(progressAmount);

      // Update exp bar
      this.updateExpBar(towerId);

      // Update dance visuals for all dancers
      for (const playerId of dancers) {
        this.updateDanceVisuals(playerId, coOpDancing);
      }
    }
  }
  
  /**
   * Get upgrade time for a specific level (ms).
   * Level 5->6 (rainbow) is 10x longer than 4->5 and is dance-only.
   */
  private getUpgradeTime(currentLevel: number): number {
    if (currentLevel === 5) {
      // Rainbow: 10x the time it takes to go from level 4 to 5
      const level4To5Ms = DANCE_CONFIG.baseTimeSeconds * Math.pow(DANCE_CONFIG.timeMultiplierPerLevel, 3) * 1000;
      return level4To5Ms * DANCE_CONFIG.rainbowTimeMultiplier;
    }
    const multiplier = Math.pow(DANCE_CONFIG.timeMultiplierPerLevel, currentLevel - 1);
    return DANCE_CONFIG.baseTimeSeconds * multiplier * 1000; // ms
  }

  /**
   * Get all players dancing on a specific tower
   */
  getDancersOnTower(towerId: string): PlayerId[] {
    const dancers: PlayerId[] = [];
    for (const [playerId, dancing] of this.dancingPlayers) {
      if (dancing.towerId === towerId) {
        dancers.push(playerId);
      }
    }
    return dancers;
  }

  // ============================================
  // Private Methods
  // ============================================

  private startDance(playerId: PlayerId, towerId: string): void {
    const dancing: DancingPlayer = {
      playerId,
      towerId,
      startTime: Date.now(),
    };
    this.dancingPlayers.set(playerId, dancing);

    // Lock player in place
    const player = this.getPlayer(playerId);
    if (player) {
      player.setDancing(true);
    }

    // Create dance visuals
    this.createDanceVisuals(playerId, towerId);

    // Show exp bar
    this.showExpBar(towerId);

    // Callback
    this.onDanceStart?.(playerId, towerId);

    console.log(`[DanceSystem] Player ${playerId} started dancing on tower ${towerId}`);
  }

  private findTowerAtPlayer(player: Player): Tower | null {
    const towers = EntityManager.getByType<Tower>('tower');
    const playerX = player.x;
    const playerY = player.y;

    // Find tower within range
    const dancingRange = 50;
    for (const tower of towers) {
      const dist = Phaser.Math.Distance.Between(playerX, playerY, tower.x, tower.y);
      if (dist <= dancingRange) {
        return tower;
      }
    }

    return null;
  }

  private getPlayer(playerId: PlayerId): Player | null {
    const players = EntityManager.getByType<Player>('player');
    for (const player of players) {
      if (player.getPlayerId() === playerId) {
        return player;
      }
    }
    return null;
  }

  // ============================================
  // Dance Visuals
  // ============================================

  private createDanceVisuals(playerId: PlayerId, towerId: string): void {
    if (!this.scene) return;

    const player = this.getPlayer(playerId);
    if (!player) return;

    // Create "Dancing..." text
    const text = this.scene.add.text(player.x, player.y - 50, 'Dancing +EXP', {
      fontSize: '12px',
      color: '#ff88ff',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    });
    text.setOrigin(0.5);
    text.setDepth(RENDER_LAYERS.UI_WORLD);
    this.danceTexts.set(playerId, text);
  }

  private updateDanceVisuals(playerId: PlayerId, coOpDancing: boolean): void {
    const player = this.getPlayer(playerId);
    const text = this.danceTexts.get(playerId);

    if (player && text) {
      // Update text position (player handles its own dance animation now)
      text.setPosition(player.x, player.y - 50);

      // Show co-op bonus
      if (coOpDancing) {
        text.setText('Dancing +EXP x1.6!');
        text.setColor('#ffff00');
      } else {
        text.setText('Dancing +EXP');
        text.setColor('#ff88ff');
      }

      // Bounce animation for text only
      const bounce = Math.sin(Date.now() / 150) * 3;
      text.y += bounce;
    }
    // Note: Player now handles its own frame-based dance animation synced to BPM
  }

  private cleanupDanceVisuals(playerId: PlayerId): void {
    // Clean up text
    const text = this.danceTexts.get(playerId);
    if (text) {
      text.destroy();
      this.danceTexts.delete(playerId);
    }

    // Clean up particles
    const particles = this.danceParticles.get(playerId);
    if (particles) {
      particles.stop();
      particles.destroy();
      this.danceParticles.delete(playerId);
    }
  }

  // ============================================
  // Exp Bar UI
  // ============================================

  private showExpBar(towerId: string): void {
    if (!this.scene) return;
    if (this.expBars.has(towerId)) return;

    const tower = EntityManager.get<Tower>(towerId);
    if (!tower) return;

    // Create vertical exp bar container
    const container = this.scene.add.container(tower.x + 30, tower.y);
    container.setDepth(RENDER_LAYERS.UI_WORLD);

    // Background
    const bg = this.scene.add.rectangle(0, 0, 8, 50, 0x000000, 0.7);
    bg.setStrokeStyle(1, 0xff88ff);
    bg.setOrigin(0.5, 1);

    // Fill bar
    const fill = this.scene.add.rectangle(0, 0, 6, 0, 0xff88ff);
    fill.setOrigin(0.5, 1);

    // Level text (use tower's level directly)
    const levelText = this.scene.add.text(0, 5, `L${tower.getLevel()}`, {
      fontSize: '10px',
      color: '#ffffff',
    });
    levelText.setOrigin(0.5, 0);

    container.add([bg, fill, levelText]);
    container.setData('fill', fill);
    container.setData('levelText', levelText);

    this.expBars.set(towerId, container);
  }

  private updateExpBar(towerId: string): void {
    const container = this.expBars.get(towerId);
    if (!container) return;

    const tower = EntityManager.get<Tower>(towerId);
    if (!tower) return;

    // Use tower's progress directly (shared with kills and TowerInteractionSystem)
    const progress = tower.getUpgradeProgress();
    const fill = container.getData('fill') as Phaser.GameObjects.Rectangle;
    const levelText = container.getData('levelText') as Phaser.GameObjects.Text;

    if (fill) {
      fill.setSize(6, 48 * progress);
    }

    const level = tower.getLevel();
    const isMaxLevel = level >= DANCE_CONFIG.maxLevel;
    if (levelText) {
      if (isMaxLevel) {
        levelText.setText('MAX');
      } else {
        levelText.setText(`L${level}`);
      }
    }
  }

  private hideExpBar(towerId: string): void {
    const container = this.expBars.get(towerId);
    if (container) {
      container.destroy();
      this.expBars.delete(towerId);
    }
  }

  private showMaxLevelIndicator(tower: Tower): void {
    if (!this.scene) return;

    const text = this.scene.add.text(tower.x, tower.y - 40, 'MAX LEVEL', {
      fontSize: '12px',
      color: '#ffff00',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    });
    text.setOrigin(0.5);
    text.setDepth(RENDER_LAYERS.UI_WORLD);

    this.scene.tweens.add({
      targets: text,
      y: tower.y - 70,
      alpha: 0,
      duration: 1000,
      onComplete: () => text.destroy(),
    });
  }

  // ============================================
  // Cleanup
  // ============================================

  reset(): void {
    // Stop all dancing
    for (const playerId of this.dancingPlayers.keys()) {
      this.stopDance(playerId);
    }

    // Clean up exp bars
    for (const container of this.expBars.values()) {
      container.destroy();
    }
    this.expBars.clear();
  }

  destroy(): void {
    this.reset();
    this.scene = null;
  }
}

// Singleton
export const DanceSystem = new DanceSystemManager();
